import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import TaskNotificationProvider from './TaskNotificationProvider';
import PushNotificationSettings from './PushNotificationSettings';
import {
  LayoutDashboard,
  Home,
  ClipboardList,
  Users,
  UserCircle,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Search,
  Monitor,
  Upload,
  Mail,
  SprayCan,
  Wrench,
  Tag,
  Shield,
  Settings,
  Key,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/safeToast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = "/api";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  
  // Password Change Dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  // Version Info laden
  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch(`/version.json?_=${Date.now()}`, { 
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          setVersionInfo(data);
        }
      } catch (error) {
        console.warn('Version info load failed:', error);
      }
    };
    fetchVersionInfo();
  }, []);
  
  // Version-String formatieren
  const getVersionDisplay = () => {
    if (!versionInfo) return null;
    
    // Base Version extrahieren (z.B. "2.1.0" aus "2.1.20260203.1")
    const version = versionInfo.version || '';
    const parts = version.split('.');
    const baseVersion = parts.length >= 2 ? `${parts[0]}.${parts[1]}.0` : version;
    
    // Datum formatieren
    let dateStr = '';
    if (versionInfo.buildDate) {
      const date = new Date(versionInfo.buildDate);
      dateStr = date.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    }
    
    return dateStr ? `Version ${baseVersion} · ${dateStr}` : `Version ${baseVersion}`;
  };
  
  // Defensive check - if no user, don't render (should be caught by ProtectedRoute)
  // This must be AFTER all hooks
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validierung
    if (passwordData.new_password.length < 6) {
      toast.error('Das neue Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, passwordData);
      toast.success('Passwort erfolgreich geändert');
      setPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    
    switch (result.type) {
      case 'property':
        navigate(`/properties/${result.id}`);
        break;
      case 'owner':
        navigate(`/owners?highlight=${result.id}`);
        break;
      case 'task':
        navigate(`/tasks/${result.id}`);
        break;
      default:
        break;
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Monitoring', href: '/monitoring', icon: Monitor },
    { name: 'Aufgaben', href: '/tasks', icon: ClipboardList },
    { name: 'Objekte', href: '/properties', icon: Home },
    { name: 'Eigentümer', href: '/owners', icon: UserCircle },
  ];

  // Admin-Menüpunkte in der gewünschten Reihenfolge hinzufügen
  if (user?.role === 'admin') {
    // Reinigungsfirmen nach Eigentümer einfügen
    navigation.push({ name: 'Reinigungsfirmen', href: '/cleaning-companies', icon: SprayCan });
    navigation.push({ name: 'Handwerksfirmen', href: '/contractors', icon: Wrench });
    navigation.push({ name: 'Tags', href: '/tags', icon: Tag });
    navigation.push({ name: 'Benutzer', href: '/users', icon: Users });
    // Import und E-Mail Admin separat am Ende (Admin-Tools)
    navigation.push({ name: 'Import', href: '/import', icon: Upload });
    navigation.push({ name: 'E-Mail Admin', href: '/email-admin', icon: Mail });
    navigation.push({ name: 'Backup', href: '/backup', icon: Shield });
    navigation.push({ name: 'Einstellungen', href: '/settings', icon: Settings });
  }

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  const getStatusLabel = (status) => {
    const labels = { neu: 'Neu', in_bearbeitung: 'In Bearbeitung', erledigt: 'Erledigt' };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = { property: 'Objekt', owner: 'Eigentümer', task: 'Aufgabe' };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#0E4C92] text-white transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-[#0A3A70]">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#0E4C92] font-bold text-sm">GB</span>
              </div>
              <span className="font-semibold text-lg font-['Manrope']">Sylt</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-[#0A3A70]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-white text-[#0E4C92] font-medium'
                      : 'text-blue-100 hover:bg-[#0A3A70] hover:text-white'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#0A3A70]">
            <div className="flex items-center px-4 py-3 mb-2">
              <div className="w-10 h-10 bg-[#0A3A70] rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-blue-200 truncate">@{user?.username}</p>
              </div>
            </div>
            {/* Push-Benachrichtigungen Toggle */}
            <PushNotificationSettings compact />
            <Button
              variant="ghost"
              className="w-full justify-start text-blue-100 hover:text-white hover:bg-[#0A3A70] mb-1"
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              Passwort ändern
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start text-blue-100 hover:text-white hover:bg-[#0A3A70]"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
            
            {/* Versionsanzeige */}
            {versionInfo && (
              <div 
                className="mt-3 pt-3 border-t border-[#0A3A70] text-center"
                data-testid="sidebar-version-info"
              >
                <p className="text-xs text-blue-200/70">
                  {getVersionDisplay()}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center h-16 px-4 lg:px-8 gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100 lg:hidden flex-shrink-0"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
            
            {/* Search Button */}
            <div className="flex-1 flex justify-center lg:justify-start">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-slate-500 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors w-full max-w-md"
                data-testid="global-search-btn"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Suchen...</span>
                <kbd className="hidden sm:inline-flex ml-auto text-xs bg-white px-2 py-0.5 rounded border">
                  ⌘K
                </kbd>
              </button>
            </div>
            
            <div className="flex items-center space-x-2 lg:hidden flex-shrink-0">
              <div className="w-8 h-8 bg-[#0E4C92] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">GB</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Globale Suche</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Objekt, Eigentümer oder Aufgabe suchen..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-slate-50"
                autoFocus
                data-testid="global-search-input"
              />
            </div>
            
            {searching && (
              <div className="text-center py-4 text-slate-500">
                Suche läuft...
              </div>
            )}
            
            {!searching && searchResults.length > 0 && (
              <div className="max-h-80 overflow-y-auto space-y-1">
                {searchResults.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}-${index}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-between"
                    data-testid={`search-result-${result.id}`}
                  >
                    <div>
                      <div className="font-medium text-slate-900">{result.title}</div>
                      <div className="text-sm text-slate-500">{result.subtitle}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {result.status && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          result.status === 'neu' ? 'bg-blue-100 text-blue-700' :
                          result.status === 'in_bearbeitung' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {getStatusLabel(result.status)}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                Keine Ergebnisse gefunden
              </div>
            )}
            
            {!searching && searchQuery.length < 2 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                Mindestens 2 Zeichen eingeben
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Key className="w-5 h-5" />
              Passwort ändern
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Aktuelles Passwort</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="bg-stone-50 pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="bg-stone-50 pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-stone-500">Mindestens 6 Zeichen</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  className="bg-stone-50 pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                }}
                className="flex-1 rounded-lg"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
                disabled={changingPassword}
                data-testid="submit-password-change"
              >
                {changingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Passwort ändern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Aufgaben-Benachrichtigungen im Hintergrund */}
      <TaskNotificationProvider />
    </div>
  );
};

export default Layout;
